import axios from 'axios';
import { installNavigationCache } from './navigationCache';

const apiBaseURL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const axiosInstance = axios.create({
  baseURL: apiBaseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const invalidateNavigation = typeof axiosInstance.get === 'function' ? installNavigationCache(axiosInstance) : () => {};

// Never refresh credentials in response to an unrelated third-party request.
const isApiRequest = (config) => {
  try {
    const api = new URL(apiBaseURL, window.location.origin);
    const base = config.baseURL || apiBaseURL;
    const request = new URL(/^https?:\/\/|^\/\//i.test(config.url || '')
      ? config.url : `${base.replace(/\/$/, '')}/${(config.url || '').replace(/^\//, '')}`,
    window.location.origin);
    return request.origin === api.origin &&
      (request.pathname === api.pathname || request.pathname.startsWith(`${api.pathname}/`));
  } catch { return false; }
};

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Request interceptor – attach access token + CSRF token + fix Content-Type for FormData
const installRequestInterceptor = client => client.interceptors.request.use(
  (config) => {
    config.headers ||= {};
    const token = localStorage.getItem('token');
    if (!isApiRequest(config)) {
      const inheritedToken = axios.defaults.headers.common.Authorization;
      if (config.headers.Authorization === inheritedToken || config.headers.Authorization === `Bearer ${token}`) {
        delete config.headers.Authorization;
      }
      delete config.headers['X-CSRF-Token'];
      return config;
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    // CSRF: Read cookie and send as header
    const csrfToken = document.cookie.split('; ').find(c => c.startsWith('csrf_token='))?.split('=')[1];
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    // Let axios auto-set Content-Type for FormData (multipart/form-data with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor – auto-refresh on 401 + fire 'library:updated' on library writes
const installResponseInterceptor = (client) => client.interceptors.response.use(
  (response) => {
    // Bibliothek-Refresh: feuere ein Event, wenn ein PDF/Datei in die Bibliothek gespeichert wurde.
    // Die DocumentLibrary-Komponente hört darauf und lädt sich automatisch neu — kein Page-Reload nötig.
    try {
      const url = response?.config?.url || '';
      const method = (response?.config?.method || '').toLowerCase();
      const isWrite = ['post', 'put', 'patch', 'delete'].includes(method);
      if (isWrite && isApiRequest(response.config)) invalidateNavigation();
      const isLibrary = /\/documents\/(library|folders)/.test(url);
      const isAssignment = /\/assignments(\b|\/)/.test(url);
      if (isWrite && isLibrary) {
        window.dispatchEvent(new CustomEvent('library:updated'));
      }
      if (isWrite && isAssignment) {
        window.dispatchEvent(new CustomEvent('assignments:updated'));
      }
    } catch (e) { /* never break the response chain */ }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest || !isApiRequest(originalRequest)) return Promise.reject(error);

    // Skip refresh for login/register/refresh endpoints
    const skipPaths = ['/auth/login', '/auth/register', '/auth/refresh'];
    if (skipPaths.some(p => originalRequest.url?.includes(p))) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/';
        return Promise.reject(error);
      }

      // Every request gets at most one retry, including queued requests.
      originalRequest._retry = true;
      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          (originalRequest.headers ||= {}).Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const res = await axios.post(
          '/auth/refresh',
          { refresh_token: refreshToken },
          { baseURL: apiBaseURL, timeout: 15000 }
        );

        // A logout or a different login while refresh was pending must win.
        if (localStorage.getItem('refreshToken') !== refreshToken) {
          throw new Error('Session changed during token refresh');
        }
        const { access_token, refresh_token: newRefresh } = res.data;
        localStorage.setItem('token', access_token);
        localStorage.setItem('refreshToken', newRefresh);

        axiosInstance.defaults.headers.common.Authorization = `Bearer ${access_token}`;
        // Older screens still use the default axios client. Keep it in sync.
        axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
        (originalRequest.headers ||= {}).Authorization = `Bearer ${access_token}`;

        processQueue(null, access_token);
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // A network outage or server error does not invalidate a session.
        if ([401, 403].includes(refreshError.response?.status)) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          delete axiosInstance.defaults.headers.common.Authorization;
          delete axios.defaults.headers.common.Authorization;
          window.location.href = '/';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

installRequestInterceptor(axiosInstance);
installRequestInterceptor(axios);
installResponseInterceptor(axiosInstance);
installResponseInterceptor(axios);

export default axiosInstance;
